import { chatStateAtom } from '@/atoms/chatAtoms';
import type { ChatResponse } from '@/types';
import { parseIsoMaybe } from '@/utils/dates';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { convertChatMessagesToTimeline } from '../utils/convertChatMessages';

const POLL_INTERVAL_MS = 3_000;
/** Default page-load resume window — backend may still be finalising. */
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
/**
 * Mid-stream recovery after an SSE drop — keep V&V-friendly (REG-51 / SWR-35).
 * Longer hangs previously left users on "Reconnecting…" with no error.
 */
export const MID_STREAM_MAX_POLL_DURATION_MS = 45_000;
const STALENESS_THRESHOLD_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

interface StartRecoveryOptions {
  onFallback: () => void;
  /** Override max poll window (defaults to page-load 5 min). */
  maxPollDurationMs?: number;
}

interface UseStreamRecoveryParams {
  stateKey: string;
}

export function useStreamRecovery({ stateKey }: UseStreamRecoveryParams) {
  const [isRecovering, setIsRecovering] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const setChatState = useSetAtom(chatStateAtom);
  const activeRecoveryRef = useRef(false);

  const stopRecovery = useCallback(() => {
    activeRecoveryRef.current = false;
    consecutiveFailuresRef.current = 0;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsRecovering(false);
  }, []);

  const startRecovery = useCallback(
    (chatId: string, { onFallback, maxPollDurationMs }: StartRecoveryOptions) => {
      if (activeRecoveryRef.current) {
        stopRecovery();
      }
      activeRecoveryRef.current = true;
      consecutiveFailuresRef.current = 0;
      setIsRecovering(true);

      const pollDuration = maxPollDurationMs ?? MAX_POLL_DURATION_MS;

      logger.info('[stream-recovery] Starting recovery polling', {
        chatId,
        stateKey,
        maxPollDurationMs: pollDuration,
      });

      const poll = async () => {
        if (!activeRecoveryRef.current) return;

        try {
          const { data } = await scribeHttpV2.get<ChatResponse>(
            `/chat/chats/${chatId}`
          );

          if (!activeRecoveryRef.current) return;

          consecutiveFailuresRef.current = 0;

          if (data.completion_in_progress) {
            const updatedAt = parseIsoMaybe(data.updated_at)?.getTime() ?? Date.now();
            const age = Date.now() - updatedAt;

            if (age > STALENESS_THRESHOLD_MS) {
              logger.warn('[stream-recovery] Stale completion_in_progress, giving up', {
                chatId,
                ageMs: age,
              });
              stopRecovery();
              onFallback();
              return;
            }
            return;
          }

          const lastMsg = data.messages[data.messages.length - 1];
          if (lastMsg?.role === 'assistant') {
            logger.info('[stream-recovery] Backend completed, applying recovered data', {
              chatId,
              messageCount: data.messages.length,
            });

            const timeline = convertChatMessagesToTimeline(data.messages);
            setChatState((prev) => ({
              ...prev,
              [stateKey]: {
                ...prev[stateKey],
                timeline,
                streamingState: null,
                metadata: { lastActivity: Date.now(), clientChatId: chatId },
              },
            }));
            stopRecovery();
            return;
          }

          // completion_in_progress is false but no assistant message yet — still waiting
        } catch (err) {
          consecutiveFailuresRef.current += 1;
          logger.error('[stream-recovery] Poll failed', {
            chatId,
            error: err,
            consecutiveFailures: consecutiveFailuresRef.current,
          });

          if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
            logger.warn('[stream-recovery] Too many consecutive poll failures, falling back', {
              chatId,
              consecutiveFailures: consecutiveFailuresRef.current,
            });
            stopRecovery();
            onFallback();
          }
        }
      };

      void poll();
      intervalRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);

      timeoutRef.current = setTimeout(() => {
        if (!activeRecoveryRef.current) return;
        logger.warn('[stream-recovery] Polling timed out', {
          chatId,
          maxPollDurationMs: pollDuration,
        });
        stopRecovery();
        onFallback();
      }, pollDuration);
    },
    [stateKey, setChatState, stopRecovery]
  );

  useEffect(() => {
    return () => stopRecovery();
  }, [stateKey, stopRecovery]);

  return { isRecovering, startRecovery, stopRecovery };
}
